import { CSSProperties, useContext, useEffect, useRef, useState } from "react";
import "./style.css";
import Numbering from "./Numbering";
import { FeedBackComponentProps, FeedbackPropsOffline } from "./types";
import Emoji from "./Emoji";
import Star from "./Star";
import Like from "./Like";
import QuestWrapper from "../../../../components/QuestWrapper";
import Success from "./Success";
import { cross } from "../../../../assets/assetsSVG";
import Loader from "../../../../components/Login/Loader";
import InlineFeedbackThemeManager from "./InlineFeedbackThemeManager";

const componentMapping: {
  [key in string]: (props: FeedBackComponentProps) => JSX.Element;
} = {
  number: Numbering,
  like: Like,
  emoji: Emoji,
  star: Star,
};

export default function InlineFeedbackOffline({
  heading = "Found it helpful",
  description = "Your feedback helps us improve search results!",
  Headers,
  type = "number",
  count = 5,
  styleConfig,
  footerText = "Powered by PLG OS",
  onChange,
  onRequestClose,
  initialState,
  offlineFormData,
  setAnswer,
  footerLink,
  BrandTheme,
  QuestThemeData,
  showFooter = true,
}: FeedbackPropsOffline) {
  const [questData, setQuestData] = useState<any | null>(null);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const { themeConfig } = useContext(QuestWrapper.Context);
  const data = useRef<object>();

  const themeManager = new InlineFeedbackThemeManager(
    themeConfig,
    styleConfig,
    QuestThemeData,
    BrandTheme
  );

  const Component = componentMapping[offlineFormData[0]?.metadata?.ratingType || type];

  useEffect(() => {
    setQuestData(offlineFormData);
  }, [offlineFormData]);

  const handleRatingClick = (data: any) => {
    const answer = type === "like" ? (data?.like ? "like" : "dislike") : `${data.rate}/${data.total}`;
    setAnswer?.({ criteriaId: questData?.[0]?.criteriaId, answers: [answer] });
    setTimeout(() => setShowSuccess(true), 1000);
  };

  const onDataChange = (updatedData: object) => {
    data.current = updatedData;
    handleRatingClick(updatedData);
    onChange?.(updatedData);
  };



  const renderHeader = () => (
    <div className="feedbackHeader">
      <div className="inline-text-cont" style={{ background: "transparent" }}>
        <div className="inline-head" style={themeManager.getHeadingStyles()}>
          {Headers?.[0]?.heading || heading}
        </div>
        <div className="inline-desc" style={themeManager.getDescriptionStyles()}>
          {Headers?.[0]?.subHeading || description}
        </div>
      </div>
      <div onClick={onRequestClose} className="inline-cross-btn">
        {cross(themeManager.getCloseIconColor())}
      </div>
    </div>
  );

  const renderFeedbackSection = () => (
    <section className="feedbackSection">
      <Component
        onChange={onDataChange}
        count={count}
        style={themeManager.getActionStyles().container}
        buttonStyle={themeManager.getActionStyles().button}
        selectedButtonStyle={themeManager.getActionStyles().selectedButton}
        iconStyle={themeManager.getActionStyles().icon}
        selectedIconStyle={themeManager.getActionStyles().selectedIcon}
        initialState={initialState}
      />
      <div className="feedbackMessage">
        <span style={{ color: BrandTheme?.titleColor }}>{questData?.[0]?.metadata?.leftRatingText || 'Not satisfied'}</span>
        <span style={{ color: BrandTheme?.titleColor }}>{questData?.[0]?.metadata?.rightRatingText || 'Very satisfied'}</span>
      </div>
    </section>
  );

  const renderFooter = () => (
    <div
      className="feedbackWatermark"
      onClick={() => window.open(footerLink || "https://www.plgos.com/")}
      style={themeManager.getFooterStyles()}
    >
      <p>{footerText}</p>
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 0V4L8 8V4H4V6.64083C4 7.39167 4.60833 8 5.35917 8H8L4 12C1.79083 12 0 10.2092 0 8V0H12Z" fill="#B9B9B9" />
        <path d="M12 8L8 8L8 12H12V8Z" fill="#B9B9B9" />
      </svg>
    </div>
  );

  return questData && (
    <div className="feedbackContainer" data-type={type} style={themeManager.getContainerStyles()}>
      {showSuccess ? (
        <Success closeFeedback={() => setShowSuccess(false)} themeManager={themeManager} />
      ) : (
        <>
          {renderHeader()}
          {renderFeedbackSection()}
        </>
      )}
      {showFooter && renderFooter()}
      {isLoading && <Loader />}
    </div>
  );
}